import { useForm, UseFormReturn } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { isNull, merge, omitBy } from 'lodash';

import { RecursivePartial } from '@pimm/base';
import { isFutureDate, isValidMonthDayOrEmpty, isValidOrEmptyDate, PHONE_REGEX } from '../utils';

// Masked-date validation, matching Add Employee: Birthday is year-less
// (MM / DD only, no future check — a birthday recurs every year, so
// "future" isn't meaningful without a year to compare against). Hire Date
// keeps the full masked MM / DD / YYYY date with a future check.
export const FormEmployeeRecordSchema = yup.object({
  firstName: yup.string().required('store_settings:first_name_required').default(''),
  lastName: yup.string().required('store_settings:last_name_required').default(''),
  title: yup.string().optional().default(''),
  birthDate: yup
    .string()
    .optional()
    .default('')
    .test('valid-birthday', 'store_settings:invalid_birthday', v => isValidMonthDayOrEmpty(v ?? '')),
  hireDate: yup
    .string()
    .optional()
    .default('')
    .test('valid-date', 'store_settings:invalid_date', v => isValidOrEmptyDate(v ?? ''))
    .test('not-future', 'store_settings:date_future', v => !isFutureDate(v ?? '')),
  email: yup.string().optional().email('store_settings:invalid_email').default(''),
  mobileNumber: yup
    .string()
    .optional()
    .default('')
    .matches(PHONE_REGEX, { message: 'store_settings:invalid_phone', excludeEmptyString: true })
    .test('phone-digit-count', 'store_settings:invalid_phone', v => {
      if (!v) return true;
      const digits = v.replace(/\D/g, '').length;
      return digits >= 7 && digits <= 15;
    }),
});

export type FormDataEmployeeRecord = yup.InferType<typeof FormEmployeeRecordSchema>;
export type FormEmployeeRecordReturn = UseFormReturn<FormDataEmployeeRecord>;

export type FormEmployeeRecordProps = {
  defaultValues?: RecursivePartial<FormDataEmployeeRecord>;
};

export const useFormEmployeeRecord = (props?: FormEmployeeRecordProps): FormEmployeeRecordReturn => {
  return useForm<FormDataEmployeeRecord>({
    defaultValues: merge(FormEmployeeRecordSchema.getDefault(), omitBy(props?.defaultValues, isNull)),
    mode: 'onTouched',
    resolver: yupResolver(FormEmployeeRecordSchema),
  });
};
